import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type {
  Auction,
  AuctionChangePaymentMethod,
  AuctionParticipantStatusEnum,
  AuctionTakeInstantOrder, AuctionsExportRetrieveParams, AuctionsListParams,
  PatchedAuctionUpdate, AuctionsListToMergeListParams, AuctionUnlinkMerged,
} from '@apiTypes'
import type { InstantOrderRequest, InstantOrderResponse } from '@/views/instant-order/types/instant-order-data'

interface AuctionsState {
  instantOrderRequest?: InstantOrderRequest
  instantOrderResponse?: InstantOrderResponse
}

export const useAuctionsStore = defineStore('auctions', {
  state: (): AuctionsState => ({
    instantOrderRequest: undefined,
    instantOrderResponse: undefined,
  }),
  actions: {
    setInstantOrderData(request: InstantOrderRequest, response: InstantOrderResponse) {
      this.instantOrderRequest = request
      this.instantOrderResponse = response
    },
    popInstantOrderRequest() {
      const instantOrderRequest = this.instantOrderRequest

      this.instantOrderRequest = undefined

      return instantOrderRequest
    },
    popInstantOrderResponse() {
      const instantOrderResponse = this.instantOrderResponse

      this.instantOrderResponse = undefined

      return instantOrderResponse
    },
    fetchAuctions(query: AuctionsListParams) {
      return api.auctions.auctionsList(query)
    },
    fetchMergedAuctions(id: number) {
      return api.auctions.auctionsMergedList({ id }).then(response => response.data)
    },
    fetchMergeAuctions(query: AuctionsListToMergeListParams) {
      return api.auctions.auctionsListToMergeList(query).then(response => response.data)
    },
    fetchAuction(id: number) {
      return api.auctions.auctionsRetrieve({ id })
    },
    fetchTransport(id: number) {
      return api.transports.transportsRetrieve({ id }).then(response => response.data)
    },
    fetchCarrierAuction(hash: string, contactHash: string) {
      return api.byHash.byHashContactsAuctionsRetrieve({ contactHash, hash }, { skipAuthToken: true }).then(response => response.data)
    },
    publishAuction(id: number) {
      return api.auctions.auctionsPublishCreate({ id }, {} as Auction)
    },
    takeInstantOrder(id: number, data: AuctionTakeInstantOrder) {
      return api.auctions.auctionsTakeInstantOrderCreate({ id }, data)
    },
    changePaymentMethod(id: number, data: AuctionChangePaymentMethod) {
      return api.auctions.auctionsChangePaymentMethodCreate({ id }, data)
    },
    cancelAuction(id: number) {
      return api.auctions.auctionsCancelCreate({ id }, {} as Auction)
    },
    unlinkMerged(id: number, data: AuctionUnlinkMerged) {
      return api.auctions.auctionsUnlinkMergedCreate({ id }, data)
    },
    updateContacts(id: number, contactsIds: number[]) {
      return api.auctions.auctionsPartialUpdate({ id }, { invitedContacts: contactsIds })
    },
    extendDuration(id: number, duration: string) {
      return api.auctions.auctionsPartialUpdate({ id }, { duration })
    },
    updateAuction(id: number, data: PatchedAuctionUpdate) {
      return api.auctions.auctionsPartialUpdate({ id }, data)
    },
    updateCarrierStatus(hash: string, contactHash: string, status: AuctionParticipantStatusEnum) {
      return api.byHash.byHashContactsAuctionsStatusCreate({ contactHash, hash }, { status }, { skipAuthToken: true })
    },
    deleteAuction(id: number, includeMerged: boolean) {
      return api.auctions.auctionsDestroy({ id, includeMerged })
    },
    exportAuctions(params: AuctionsExportRetrieveParams) {
      return api.auctions.auctionsExportRetrieve(params, { format: 'blob' })
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useAuctionsStore, import.meta.hot))
